/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EndpointsPanel,
  EndpointsPanelLink,
} from '@modules/resources-shared-components/EndpointsPanel/EndpointsPanel'
import { AnyUserProject } from '@modules/project-user-api/types'
import { getKibanaRedirectPageUrl } from '@modules/kibana-redirect/url'
import { getProjectEndpoint } from '@modules/project-lib/getProjectEndpoint'

const messages = defineMessages({
  elasticsearch: {
    id: 'project-endpoints-panel.elasticsearch',
    defaultMessage: 'Elasticsearch',
  },
  kibana: {
    id: 'project-endpoints-panel.kibana',
    defaultMessage: 'Kibana',
  },
  apm: {
    id: 'project-endpoints-panel.apm',
    defaultMessage: 'APM',
  },
  ingest: {
    id: 'project-endpoints-panel.ingest',
    defaultMessage: 'Ingest',
  },
})

interface ProjectEndpointsPanelProps {
  project: AnyUserProject
  onboardingToken?: string
}

export const ProjectEndpointsPanel = (props: ProjectEndpointsPanelProps) => {
  const { project, onboardingToken } = props

  const {
    hasPrivateLink,
    hasIpFilter,
    endpoints: publicEndpoints,
    private_endpoints: privateEndpoints,
  } = project

  const { formatMessage } = useIntl()

  const href = getKibanaRedirectPageUrl(project.type, project.id, onboardingToken)

  const endpoints = getProjectEndpoint(project)

  const showPublicUrl = !hasPrivateLink || hasIpFilter

  const showPrivateUrl = !hasIpFilter || hasPrivateLink

  const getFormattedLinks = (): EndpointsPanelLink[] => {
    const formattedLinks: EndpointsPanelLink[] = []

    Object.keys(endpoints).forEach((appType) => {
      formattedLinks.push({
        appType: appType as EndpointsPanelLink['appType'],
        isEnabled: true,
        showOpenButton: true,
        label: formatMessage(messages[appType]),
        url: showPublicUrl && publicEndpoints[appType],
        // We want to show the private url only if the project:
        // - The project should use private endpoints
        // - The project has both a private link traffic filter and an IP traffic filter
        privateUrl: showPrivateUrl && privateEndpoints?.[appType],
        openUrl: appType === 'kibana' ? href : undefined,
      })
    })

    return formattedLinks
  }

  return <EndpointsPanel links={getFormattedLinks()} />
}
